import type {
  Gallery,
  GalleryItem,
  GalleryPhotoShape,
  GalleryPlanFeatures,
  GallerySpacing,
} from './gallery.api';
import { allItems } from './gallery-view';

// The values every Plan may choose (spec decision 2): the design's own gap, square tiles.
export const FREE_GALLERY_SPACING: GallerySpacing = 'DESIGN';
export const FREE_GALLERY_PHOTO_SHAPE: GalleryPhotoShape = 'SQUARE';

// The API's cap for an uploaded cover (JPG or PNG, 50 MB).
export const GALLERY_COVER_MAX_BYTES = 50 * 1024 * 1024;
const GALLERY_COVER_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

// One option of a paid choice: on a Plan without the feature only the free value can be chosen,
// so a value kept from before a downgrade shows as chosen and can go back to the free one
// (decisions 9 and 10). Features not loaded yet lock nothing; the API refuses anyway.
export function isPaidOptionLocked<T>(
  features: Partial<GalleryPlanFeatures> | null | undefined,
  feature: keyof GalleryPlanFeatures,
  option: T,
  freeValue: T
): boolean {
  return features?.[feature] === false && option !== freeValue;
}

// Photos the Provider can make the cover: ready images with a thumbnail.
export function coverCandidates(gallery: Pick<Gallery, 'sections'>): GalleryItem[] {
  return allItems(gallery).filter(
    (item) => item.kind === 'IMAGE' && item.state === 'READY' && Boolean(item.thumb400Url)
  );
}

export function checkGalleryCoverFile(file: File): 'fileType' | 'tooLarge' | null {
  if (!GALLERY_COVER_TYPES.includes(file.type.toLowerCase())) {
    return 'fileType';
  }
  return file.size > GALLERY_COVER_MAX_BYTES ? 'tooLarge' : null;
}

// What the page keeps asking the API about while the Design view is open: an uploaded cover being
// made, or watermarked copies still being drawn.
export function isDesignWorkPending(
  gallery: Pick<Gallery, 'cover' | 'watermark' | 'watermarkProgress'>
): boolean {
  if (gallery.cover?.state === 'PROCESSING') {
    return true;
  }
  const progress = gallery.watermark ? gallery.watermarkProgress : null;
  return Boolean(progress && progress.ready + progress.failed < progress.total);
}

// The preview iframe draws the page at a real device width and is scaled down to fit its column.
export const PREVIEW_FRAMES = {
  desktop: { width: 1280, height: 800 },
  phone: { width: 390, height: 844 },
} as const;

export type PreviewFrame = keyof typeof PREVIEW_FRAMES;

// The guest page with the owner's preview token in the fragment (never sent to the CDN), so a
// password does not stand in the preview's way and nothing is counted. Without a token: the page
// as a guest sees it.
export function galleryPreviewSrc(link: string, previewToken: string | null): string {
  const base = link.split('#')[0];
  return previewToken ? `${base}#preview=${encodeURIComponent(previewToken)}` : base;
}

// When to ask for the next preview token: five minutes before this one runs out (a minute at
// least, so a clock off by a lot cannot make it ask without pause).
export const PREVIEW_TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

export function previewTokenRefreshDelay(expiresAt: string, now = Date.now()): number {
  const expiry = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiry)) {
    return 60 * 1000;
  }
  return Math.max(60 * 1000, expiry - PREVIEW_TOKEN_REFRESH_MARGIN_MS - now);
}

export function previewScale(frame: PreviewFrame, availableWidth: number): number {
  const { width } = PREVIEW_FRAMES[frame];
  if (!Number.isFinite(availableWidth) || availableWidth <= 0) {
    return frame === 'phone' ? 0.75 : 0.5;
  }
  return Math.min(1, availableWidth / width);
}
